
import React, { useEffect, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCHookToast, RCCServicesApi, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import AppIcons from "src/components/AppIcons";
import FiltersTagsContainer from "src/components/FiltersTagsContainer";
import { IRefFiltersTagsContainer, TagsTypes } from "src/components/FiltersTagsContainer/interface";
import SidebarCard from "src/components/SidebarCard";
import { IFiltersCard, IRefSidebarCard, ISidebarCardItem } from "src/components/SidebarCard/interface";
import SidebarCardFilters from "src/components/SidebarCard/sidebarCardFilters";
import { useAuth } from "src/context/Auth/context";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import ContentStrategy from "./contentStrategy";
import { IRefContentStrategy } from "./interface";

const Strategies: React.FC = () => {

	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);

	const { translate, currentLanguage } = useTranslation();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
	const { administrator , superUser } = useAuth();
  
	const refSidebarCard = useRef<IRefSidebarCard>(null);
	const refContentStrategy = useRef<IRefContentStrategy>(null);
	const { 
		handleSubmit : handleSubmitFilters, 
		register : registerFilters, 
		control:controlFilters, 
		reset: resetFilters 
	} = useForm();

	const [cards, setCards] = useState<ISidebarCardItem[]>([]);
	const [selectedMap, setselectedMap] = useState<number | null>(null);
	
	const [showTagsFilters, setShowTagsFilters] = useState(false);
	const filtersTagsRef = useRef<IRefFiltersTagsContainer>(null);
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<IFiltersCard>({ 
		f: "", 
		startDate: null,
		endDate: null,
		active: "S",
		tags: ""
	});

	async function filtersStrategies(data:IFiltersCard){
		setFilters(data);
	}

	const submitFiltersTags = async () => {
		setFilters((prevState) => {
			const updateState = {...prevState};
			updateState.tags = filtersTagsRef.current?.response ? filtersTagsRef.current?.response : "";
			return updateState;
		});
	};

	const extractSingleValueFromArrayOfObject = (array: string | undefined) => {
		if(!array)
			return undefined;
		const newArray =  array.split(",");
		if(!newArray || newArray.length === 0)
			return undefined;
		return newArray.map((x: string) => parseInt(x, 10));
	};

	async function getMaps(){
		setLoading(true);
		try{
			const result = await RCCServicesApi.getRequest({
				method: "Maps/List",
				urlParameters: {
					f: filters.f || "",
					startDate: filters.startDate ? formatDate(filters.startDate) : "",
					endDate: filters.endDate ? formatDate(filters.endDate) : "",
					active: filters.active ? filters.active : "S",
					tags: filters.tags || ""
				}
			});
			
			setCards(result.data
				.filter((e: any) => e.mea008Permissao >= 1 || administrator || superUser)
				.map((x: any) => ({
					id: x.mea001Id,
					title: x.mea001Nome,
					subtitle: mapsSubtitle(x.mea001Dtvalini, x.mea001Dtvalfim),
					filtered: true,
					selected: false,
					favorite: x.favorite,
					active: x.mea001Ativo === "S"
				})));
      
			setLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar mapas"), addToast);
			setLoading(false);
		}
		return null;
	}

	useEffect(() => {
		setselectedMap(null);
		getMaps();
	}, [filters]);

	const mapsSubtitle = (dataInit:any, dataFinale:any) => {
		const dataInicio = formatDate(new Date(dataInit), currentLanguage);
		const dataFim = formatDate(new Date(dataFinale), currentLanguage);
		return `${dataInicio} ${translate("Até")} ${dataFim}`; 
	};

	useEffect(() => {
		if(selectedMap && refContentStrategy.current)
			refContentStrategy.current?.loadData(selectedMap);
	}, [selectedMap]);

	useEffect(() => {
		const mapByUrl = objQueryParams?.map && Number(objQueryParams?.map) ? parseInt(objQueryParams?.map, 10): null;
		if(mapByUrl){
			refSidebarCard.current?.onClickCardFuncion(mapByUrl, "", null);
			refSidebarCard.current?.changeListVisibility();
		}			
	}, []);

	return (
		<>
			<SidebarCard 
				title={translate("Estratégias")}
				icon={() => <AppIcons name="analysis" />}
				ref={refSidebarCard}
				cards={cards}
				cardClick={(id) => {
					if(id){
						setselectedMap(id);
					}		
					else
						setselectedMap(null);
				}}
				filterContent={
					<>
						<SidebarCardFilters 
							filtersFunction={filtersStrategies}
							handleSubmit={handleSubmitFilters}
							reset={resetFilters}
							control={controlFilters}
							register={registerFilters}
							filters={filters}
							showFilters={showFilters}
						/>
						<FiltersTagsContainer 
							ref={filtersTagsRef}
							onSubmitFilters={submitFiltersTags}
							tagType={TagsTypes.MAPS}
							showFilters={showTagsFilters}
							defaultValue={extractSingleValueFromArrayOfObject(filters.tags)}
						/>
					</>
				}
				buttonsContent={
					<>
						<RCCTooltip
							text={translate("Tags")}
							color={showTagsFilters || filters.tags ? "blue" : "grey"}
						>
							<RCCButton
								icon={{name: "tags", color: "#FFF", size: 16}}
								color={showTagsFilters || filters.tags ? "blue" : "grey"}
								layout="isRounded"
								size="md"
								onClick={() => setShowTagsFilters((prevState) => !prevState)}
							/>
						</RCCTooltip>
						<RCCTooltip
							text={translate("Filtros")}
							color={showFilters || filters.f || filters.active !== "T" || filters.endDate || filters.startDate ? "blue" : "grey"}
						>
							<RCCButton
								icon={{name: "filter", color: "#FFF", size: 16}}
								color={showFilters || filters.f || filters.active !== "T" || filters.endDate || filters.startDate ? "blue" : "grey"}
								layout="isRounded"
								size="md"
								onClick={() => setShowFilters((prevState) => !prevState)}
							/>
						</RCCTooltip>
					</>
				}
				content={
					<>
						{selectedMap ? (
							<ContentStrategy
								ref={refContentStrategy}
							/>
						): (
							<RCCAlert
								title={translate("Nenhum mapa selecionado")}
								description={translate("Selecione um mapa para ver suas estratégias")}
							/>
						)}
					</>
				}
			/>
		</>
	);
};

export default Strategies;
